import { useState } from 'react';
import { Card, CardContent, TextField, Button, Box, Typography, MenuItem, Select, FormControl, InputLabel } from '@mui/material';
import TranslateIcon from '@mui/icons-material/Translate';
import { translateWithDeepL } from './i18n/deepl';

/**
 * Helper component for translating text using DeepL API
 * Can be used during development to quickly translate strings
 */
const TranslateHelper = () => {
    const [sourceText, setSourceText] = useState('');
    const [translatedText, setTranslatedText] = useState('');
    const [sourceLang, setSourceLang] = useState('EN');
    const [targetLang, setTargetLang] = useState('DE');
    const [loading, setLoading] = useState(false);

    const handleTranslate = async () => {
        if (!sourceText.trim()) return;

        setLoading(true);
        try {
            const result = await translateWithDeepL(sourceText, targetLang, sourceLang);
            setTranslatedText(result);
        } catch (error) {
            console.error('Translation error:', error);
            setTranslatedText('Error: Translation failed. Check console for details.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card sx={{ maxWidth: 800, margin: '20px auto' }}>
            <CardContent>
                <Box display="flex" alignItems="center" gap={2} mb={3}>
                    <TranslateIcon color="primary" />
                    <Typography variant="h5">DeepL Translation Helper</Typography>
                </Box>

                <Box display="flex" gap={2} mb={2}>
                    <FormControl sx={{ minWidth: 120 }}>
                        <InputLabel>Source</InputLabel>
                        <Select
                            value={sourceLang}
                            label="Source"
                            onChange={(e) => setSourceLang(e.target.value)}
                        >
                            <MenuItem value="EN">English</MenuItem>
                            <MenuItem value="DE">Deutsch</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl sx={{ minWidth: 120 }}>
                        <InputLabel>Target</InputLabel>
                        <Select
                            value={targetLang}
                            label="Target"
                            onChange={(e) => setTargetLang(e.target.value)}
                        >
                            <MenuItem value="DE">Deutsch</MenuItem>
                            <MenuItem value="EN">English</MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Text to translate"
                    value={sourceText}
                    onChange={(e) => setSourceText(e.target.value)}
                    margin="normal"
                    variant="outlined"
                />

                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleTranslate}
                    disabled={loading || !sourceText.trim()}
                    startIcon={<TranslateIcon />}
                    sx={{ mt: 2, mb: 2 }}
                >
                    {loading ? 'Translating...' : 'Translate'}
                </Button>

                {translatedText && (
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Translation"
                        value={translatedText}
                        margin="normal"
                        variant="outlined"
                        InputProps={{
                            readOnly: true,
                        }}
                    />
                )}

                <Typography variant="caption" color="textSecondary" sx={{ mt: 2, display: 'block' }}>
                    💡 Tip: Use this tool to quickly translate UI strings during development.
                    For batch translations, use the scripts/translate.ts script.
                </Typography>
            </CardContent>
        </Card>
    );
};

export default TranslateHelper;
